import { Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { routes } from '../../../Utils/routes';
import { LegalPage, type LegalSection } from '../LegalPage/LegalPage';

/**
 * Privacy Policy. The text is the agreement itself, carried over word for word
 * from the page it replaces: a rewrite during a rebuild would change what
 * people already agreed to.
 *
 * English only, deliberately. This is the version that binds, and translating
 * it would create a second one that might not say the same thing.
 */
const SECTIONS: LegalSection[] = [
  {
    heading: '1. Overview',
    blocks: [
      { kind: 'p', text: 'Magbit is a tool for private tutors to manage students, lessons, payments, and optional Google Calendar synchronization. This policy explains what information Magbit processes and why.' }
    ]
  },
  {
    heading: '2. Information you provide',
    blocks: [
      { kind: 'p', text: 'When you use Magbit, you may enter student names, lesson schedules, payment amounts and statuses, and notes related to your teaching activity. Do not enter sensitive personal information unless it is necessary for your own records.' }
    ]
  },
  {
    heading: '3. Account information',
    blocks: [
      { kind: 'p', text: 'Magbit uses Google Sign-In to authenticate you. We receive the Google account identifier needed to create and secure your Magbit account. Your account data is separated from other users’ data.' }
    ]
  },
  {
    heading: '4. Google Calendar access',
    blocks: [
      { kind: 'p', text: 'Calendar synchronization is optional. If you choose to connect Google Calendar, Magbit requests permission to create, read, update, and delete calendar events required to synchronize lessons in your calendar. Magbit uses this access only to provide the calendar-sync feature. You can disconnect Calendar access at any time from Magbit or from your Google Account permissions.' }
    ]
  },
  {
    heading: '5. Payments',
    blocks: [
      { kind: 'p', text: (
        <>
          Paid subscriptions (Magbit Premium) are processed by Paddle, our merchant of record. When you purchase a
          subscription, your payment details (such as card number) are collected and processed directly by Paddle under{' '}
          <a href="https://www.paddle.com/legal/privacy" rel="noopener">Paddle's privacy policy</a>; Magbit never
          receives or stores your card details. We receive from Paddle only the information needed to manage your
          subscription: its status, billing period, and identifiers linking it to your account.
        </>
      ) }
    ]
  },
  {
    heading: '6. How we use information',
    blocks: [
      { kind: 'p', text: 'We use your information to operate Magbit, store and display your teaching records, synchronize the calendar events you choose to manage through Magbit, maintain security, and respond to support requests. We do not sell personal information.' }
    ]
  },
  {
    heading: '7. Sharing',
    blocks: [
      { kind: 'p', text: 'We do not share your Magbit data with third parties except when necessary to provide the service, comply with applicable law, or protect the security of Magbit and its users. Google processes information according to its own privacy policies when you use Google Sign-In or Google Calendar, and Paddle processes payment information according to its own privacy policy when you purchase a subscription.' }
    ]
  },
  {
    heading: '8. Data retention and deletion',
    blocks: [
      { kind: 'p', text: 'Your data is retained while your account is active and for as long as needed to provide the service. You may request deletion of your account data by contacting us. Calendar events already created in your Google Calendar remain under your control and can be removed from Google Calendar.' }
    ]
  },
  {
    heading: '9. Security',
    blocks: [
      { kind: 'p', text: 'We use reasonable technical and organizational measures to protect data. No internet-based service can guarantee absolute security.' }
    ]
  },
  {
    heading: '10. Changes to this policy',
    blocks: [
      { kind: 'p', text: 'We may update this policy when the service or legal requirements change. The current version will always be published on this page.' }
    ]
  },
  {
    heading: '11. Contact',
    blocks: [
      { kind: 'p', text: (
        <>For privacy questions or deletion requests, contact <a href="mailto:rakloze@gmail.com">rakloze@gmail.com</a>.</>
      ) }
    ]
  }
];

const UPDATED = '23 July 2026';

export function Privacy() {
  const { tr } = useTranslation();
  return (
    <LegalPage
      title="Privacy Policy"
      updated={UPDATED}
      sections={SECTIONS}
      back={<Link className="ghost-button" to={routes.home}>{tr('back')}</Link>}
    />
  );
}
